import axios from "axios";
import { saveAs } from "file-saver";
import Papa from "papaparse";

// Constants
export const RequestProgressStatus = {
  Start: "START",
  End: "END",
};
export const HttpStatusCodes = {
  SSO_TOKEN_EXPIRED: 498, // Custom status code for SSO token expiration
};
export const RequestProgressEvent = "requestProgressEvent";
export const defaultErrorMessage = "Something went wrong. Please try again.";

// Shared request tracking reference
const requestsSetRef = { current: new Set() };
export const getRequestsSetRef = () => requestsSetRef;

/**
 * Rejection for a request the server answered with an error status.
 *
 * Carries the response fields consumers read, so `error.status` and
 * `error.data` read as they always have, and is an `Error` so a rejection that
 * reaches Sentry arrives with a message and a stack rather than as an anonymous
 * object with the response's keys.
 *
 * `config` and `request` are left off: the request config carries the
 * `Authorization` header.
 *
 * @param {object} response - The Axios error response.
 * @param {{ cause?: unknown }} [options]
 */
export class HttpResponseError extends Error {
  constructor(response, options) {
    super(
      `Request failed with status code ${response?.status ?? "unknown"}`,
      options
    );
    this.name = "HttpResponseError";
    this.status = response?.status;
    this.statusText = response?.statusText;
    this.data = response?.data;
    this.headers = response?.headers;
  }
}

/**
 * Base class for remote HTTP calls with built-in interceptors,
 * authentication, progress tracking, and request cancellation
 *
 * @example
 * const client = new HttpClient(
 *   'https://api.example.com',
 *   { publicPaths: [/^\/auth/], timeout: 30000 },
 *   { tokenCache, currentUserCache, appSettingsCache },
 *   { progressTimeout: 60000, progressTimeoutHandler: (url, status) => {} }
 * );
 */
class HttpClient {
  /**
   * @param {string} baseUrl - Base URL for remote calls
   * @param {object} options - Configuration options
   * @param {RegExp[]} options.publicPaths - Paths that don't require auth
   * @param {number} options.timeout - Request timeout in milliseconds
   * @param {object} dependencies - Cache dependencies
   * @param {LocalStorageCache} dependencies.tokenCache - JWT token cache
   * @param {LocalStorageCache} dependencies.currentUserCache - User cache
   * @param {LocalStorageCache} dependencies.appSettingsCache - Settings cache
   * @param {object} progressConfig - Progress tracking configuration
   * @param {number} progressConfig.progressTimeout - Progress event timeout
   * @param {Function} progressConfig.progressTimeoutHandler - Progress handler
   */
  constructor(
    baseUrl,
    { publicPaths = [], timeout = 5000 } = {},
    { tokenCache, currentUserCache, appSettingsCache } = {},
    { progressTimeout = 1000, progressTimeoutHandler } = {}
  ) {
    // Validate required parameters
    if (!baseUrl) {
      throw new Error("HttpClient requires a baseUrl");
    }

    // Initialize internal state
    this.abortControllers = new Map();
    this.singleRequestCache = new Map();
    this.requestTimers = new Map();
    this.publicPaths = publicPaths;
    this.progressTimeout = progressTimeout;
    this.progressTimeoutHandler = progressTimeoutHandler;

    // Store cache dependencies
    this.tokenCache = tokenCache;
    this.currentUserCache = currentUserCache;
    this.appSettingsCache = appSettingsCache;

    // Create axios instance
    this.http = axios.create({
      baseURL: baseUrl,
      timeout,
    });

    // Setup interceptors
    this._setupRequestInterceptor();
    this._setupResponseInterceptor();
  }

  /**
   * Get the current authentication token
   * @returns {string|null} JWT token or null
   */
  get token() {
    return this.tokenCache?.get() ?? null;
  }

  /**
   * Get the current organization code
   * @returns {string|null} Organization code or null
   */
  get organizationCode() {
    return this.currentUserCache?.get()?.organizationCode ?? null;
  }

  /**
   * Generates a unique key for URL tracking
   * @private
   */
  _getUrlKey(url, timeStamp) {
    return `${url}_${timeStamp}`;
  }

  /**
   * Setup request interceptor with authentication and progress tracking
   * @private
   */
  _setupRequestInterceptor() {
    this.http.interceptors.request.use(
      (config) => this._handleRequest(config),
      (error) => Promise.reject(error)
    );
  }

  /**
   * Handle outgoing request configuration
   * @private
   */
  _handleRequest(config) {
    // Setup abort controller for request cancellation
    const abortController = new AbortController();
    config.signal = abortController.signal;
    this.abortControllers.set(config.url, abortController);

    // Setup progress tracking
    const { timeStampHeader } = this._setupProgressTracking(config);
    config.headers["TimeStamp"] = timeStampHeader;

    // Skip authentication for public paths
    if (this._isPublicPath(config.url)) {
      return config;
    }

    // Add authentication headers
    this._addAuthHeaders(config);

    return config;
  }

  /**
   * Check if URL matches public paths (no auth required)
   * @private
   */
  _isPublicPath(url) {
    return this.publicPaths.some((urlRegex) => urlRegex.test(url));
  }

  /**
   * Add authentication headers to request
   * @private
   */
  _addAuthHeaders(config) {
    const token = this.token;
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    if (!config.headers["organization"]) {
      const orgCode = this.organizationCode;
      if (orgCode) {
        config.headers["organization"] = orgCode;
      }
    }
  }

  /**
   * Setup progress tracking for a request
   * @private
   * @returns {object} Object with urlKey, timerId, and timeStampHeader
   */
  _setupProgressTracking(config) {
    const timeStamp = Date.now();
    const timerId = setTimeout(() => {
      const urlKey = this._getUrlKey(config.url, `${timeStamp}_${timerId}`);
      this.progressTimeoutHandler?.(urlKey, RequestProgressStatus.Start);
    }, this.progressTimeout);

    const timeStampHeader = `${timeStamp}_${timerId}`;
    const urlKey = this._getUrlKey(config.url, timeStampHeader);

    this.requestTimers.set(urlKey, timerId);

    return { urlKey, timerId, timeStampHeader };
  }

  /**
   * Setup response interceptor with cleanup and error handling
   * @private
   */
  _setupResponseInterceptor() {
    this.http.interceptors.response.use(
      (response) => this._handleSuccessResponse(response),
      (error) => this._handleErrorResponse(error)
    );
  }

  /**
   * Handle successful response
   * @private
   */
  _handleSuccessResponse(response) {
    const { config, data } = response;
    const { headers, url } = config;

    this._cleanupRequest(url, headers["TimeStamp"]);

    if (config.preserveResponse) {
      return response;
    }

    return data;
  }

  /**
   * Handle error response
   * @private
   */
  _handleErrorResponse(error) {
    const { headers, url } = error.config || {};

    this._cleanupRequest(url, headers?.["TimeStamp"]);

    // Remove from requests tracking
    if (requestsSetRef.current) {
      requestsSetRef.current.delete(url);
    }

    // Handle SSO token expiration
    if (error.response?.status === HttpStatusCodes.SSO_TOKEN_EXPIRED) {
      this._handleSsoExpiration();
    }

    // A request that never got a response (network failure, timeout, abort)
    // rejects with the Axios error itself, which is already an Error and is what
    // `axios.isCancel` reads.
    return Promise.reject(
      error.response
        ? new HttpResponseError(error.response, { cause: error })
        : error
    );
  }

  /**
   * Cleanup request tracking (abort controller and timer)
   * @private
   */
  _cleanupRequest(url, timeStamp) {
    if (url) {
      this.abortControllers.delete(url);
      if (timeStamp) {
        this.clearTimer(this._getUrlKey(url, timeStamp));
      }
    }
  }

  /**
   * Handle SSO token expiration by clearing cache and redirecting
   * @private
   */
  _handleSsoExpiration() {
    /**
     * Error code 498 is sent by dashboard api when an SSO user's
     * refresh token (server session) has been revoked or expired.
     * Clear the session cache and return the user to root path
     * in unauthenticated state.
     */
    this.currentUserCache?.remove();
    this.tokenCache?.remove();
    this.appSettingsCache?.remove();
    window.location.href = window.location.origin;
  }

  /**
   * Returns the last path segment of a string, for use as a safe local
   * filename. Strips directory prefixes (e.g. from server-supplied paths) so
   * only the base name is used.
   *
   * @private
   * @param {string} [filePath]
   * @returns {string|undefined}
   */
  _fileBasename(filePath) {
    if (!filePath) {
      return filePath;
    }

    const base = filePath.replace(/^.*[/\\]/, "");
    return base || filePath;
  }

  /**
   * Reads a header from Axios `response.headers` on a successful response.
   *
   * @private
   * @param {import("axios").AxiosResponseHeaders | undefined} headers - `response.headers` from a successful Axios response
   * @param {string} name - Header name (e.g. `"content-disposition"`)
   * @returns {string | undefined} Raw header value, if present
   */
  _getHeader(headers, name) {
    if (!headers) {
      return undefined;
    }

    return headers.get(name);
  }

  /**
   * Parses a `Content-Disposition` header value and returns the suggested
   * filename, if any.
   * Handles `filename` (quoted or unquoted) and RFC 5987 `filename*=UTF-8''...`
   * (percent-encoded).
   * The result is passed through {@link HttpClient#_fileBasename} to drop any
   * path segments.
   *
   * @private
   * @param {string | undefined} headerValue - Raw `Content-Disposition` header value
   * @returns {string | null} Parsed filename, or `null` if none could be extracted
   */
  _parseContentDispositionFilename(headerValue) {
    if (!headerValue || typeof headerValue !== "string") {
      return null;
    }

    const starMatch = headerValue.match(
      /filename\*=(?:UTF-8''|utf-8'')([^;\s]+)/i
    );
    if (starMatch) {
      try {
        return this._fileBasename(decodeURIComponent(starMatch[1].trim()));
      } catch {
        return this._fileBasename(starMatch[1].trim());
      }
    }

    const quotedMatch = headerValue.match(/filename="((?:[^"\\]|\\.)*)"/i);
    if (quotedMatch) {
      return this._fileBasename(quotedMatch[1].replace(/\\"/g, '"'));
    }

    const unquotedMatch = headerValue.match(/filename=([^;\s]+)/i);
    if (unquotedMatch) {
      return this._fileBasename(unquotedMatch[1].replace(/^["']|["']$/g, ""));
    }

    return null;
  }

  /**
   * Download a file as a blob
   * @param {object} config - Axios request config
   * @param {string} [fileName] - Base name of the file to save (used with fileExtension when building the default name)
   * @param {string} [fileExtension] - File extension (default: 'csv')
   * @param {object} [options]
   * @param {BlobPropertyBag} [options.blobOptions] - Second argument to `new Blob()`; merged with default `{ type: "text/plain;charset=utf-8" }`
   * @param {boolean} [options.preferContentDispositionFilename] - When true, the saved filename prefers the value from the `Content-Disposition` response header when it can be parsed; otherwise falls back to `fileName` and `fileExtension`
   * @returns {Promise<boolean>} Resolves to `true` when the file was written via the browser download
   */
  downloadBlob(config, fileName, fileExtension = "csv", options = {}) {
    const {
      blobOptions: blobOptionsOverride,
      preferContentDispositionFilename = false,
    } = options;

    const defaultBlobPropertyBag = { type: "text/plain;charset=utf-8" };
    const blobOptions = {
      ...defaultBlobPropertyBag,
      ...blobOptionsOverride,
    };

    const requestConfig = {
      ...config,
      responseType: "blob",
      ...(preferContentDispositionFilename && { preserveResponse: true }),
    };

    return this.http.request(requestConfig).then((result) => {
      let data = result;
      let name = `${fileName ?? Date.now()}.${fileExtension}`;

      if (preferContentDispositionFilename) {
        data = result.data;

        const fromHeader = this._parseContentDispositionFilename(
          this._getHeader(result.headers, "content-disposition")
        );
        if (fromHeader) {
          name = fromHeader;
        }
      }

      const blob = new Blob([data], blobOptions);

      saveAs(blob, name);
      return true;
    });
  }

  /**
   * Parse CSV response
   * @param {object} requestConfig - Axios request config
   * @returns {Promise<object>} Parsed CSV data
   */
  parseCsvResponse(requestConfig) {
    return this.http.request(requestConfig).then((response) => {
      return Papa.parse(response, { header: true, skipEmptyLines: true });
    });
  }

  // ============================================================
  // Public Methods - Request Management
  // ============================================================

  /**
   * Cancel ongoing request(s)
   * @param {string} [url] - URL to cancel. If omitted, cancels all requests
   */
  cancelRequest(url) {
    if (url) {
      this.abortControllers.get(url)?.abort();
      this.abortControllers.delete(url);
    } else {
      // Cancel all ongoing requests
      for (const [key, controller] of this.abortControllers.entries()) {
        controller.abort();
        this.abortControllers.delete(key);
      }
    }
  }

  /**
   * Make a cached request (prevents duplicate simultaneous requests)
   * @param {string} url - Request URL
   * @param {object} config - Axios request config
   * @returns {Promise} Request promise
   */
  cachedRequest(url, config) {
    const params = config?.params;
    const cacheKey = params
      ? `${url}?${new URLSearchParams(params).toString()}`
      : url;

    if (!this.singleRequestCache.has(cacheKey)) {
      const request = this.http({ url, method: "get", ...config }).then(
        (response) => {
          this.singleRequestCache.delete(cacheKey);
          return response;
        },
        (error) => {
          // Clean up cache on error too
          this.singleRequestCache.delete(cacheKey);
          throw error;
        }
      );
      this.singleRequestCache.set(cacheKey, request);
    }

    return this.singleRequestCache.get(cacheKey);
  }

  /**
   * Clear progress tracking timer for a request
   * @param {string} url - URL key for the timer
   */
  clearTimer(url) {
    const timerId = this.requestTimers.get(url);
    if (timerId !== undefined) {
      clearTimeout(timerId);
      this.requestTimers.delete(url);
    }
    this.progressTimeoutHandler?.(url, RequestProgressStatus.End);
  }

  /**
   * Clear all cached requests
   */
  clearRequestCache() {
    this.singleRequestCache.clear();
  }

  /**
   * Get count of active requests
   * @returns {number} Number of active requests
   */
  getActiveRequestCount() {
    return this.abortControllers.size;
  }
}

export default HttpClient;
